/**
 * Source format selector for imports
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoApps, IoDocument, IoLogoGithub, IoShieldCheckmark, IoSparkles } from 'react-icons/io5';

import type { ImportSource } from '@/types/card';

export interface SourceSelectorProps {
  value: ImportSource | null;
  onChange: (source: ImportSource) => void;
  detectedSource?: ImportSource | null;
}

interface SourceOption {
  value: ImportSource;
  label: string;
  icon: ReactElement;
  description: string;
}

const SOURCE_OPTIONS: SourceOption[] = [
  {
    value: 'dragonshield',
    label: 'Dragon Shield',
    icon: <IoShieldCheckmark className="h-5 w-5" />,
    description: 'CSV export from Dragon Shield MTG app',
  },
  {
    value: 'delverlens',
    label: 'DelverLens',
    icon: <IoSparkles className="h-5 w-5" />,
    description: 'Current format with extended fields and IDs',
  },

  {
    value: 'moxfield',
    label: 'Moxfield',
    icon: <IoLogoGithub className="h-5 w-5" />,
    description: 'CSV export from Moxfield collection',
  },
  {
    value: 'archidekt',
    label: 'MTGA / Archidekt',
    icon: <IoApps className="h-5 w-5" />,
    description: 'Text format: "4 Card Name (SET) 123"',
  },
  {
    value: 'manabox',
    label: 'ManaBox',
    icon: <IoDocument className="h-5 w-5" />,
    description: 'Text or CSV export from ManaBox',
  },
];

export default function SourceSelector({ value, onChange, detectedSource }: SourceSelectorProps): ReactElement {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-pearl text-sm font-medium">Import Source</label>
        {detectedSource && (
          <span className="bg-aether/20 text-aether rounded-full px-2 py-0.5 text-xs">
            Auto-detected: {SOURCE_OPTIONS.find((o) => o.value === detectedSource)?.label}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SOURCE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={clsx(
              'flex items-start gap-3 rounded-lg border p-3 text-left transition-all',
              value === option.value
                ? 'border-aether bg-aether/10 ring-aether/50 ring-1'
                : 'border-storm hover:border-ash hover:bg-slate/50',
            )}
          >
            <div className={clsx('rounded-lg p-2', value === option.value ? 'bg-aether/20 text-aether' : 'bg-slate text-ash')}>
              {option.icon}
            </div>
            <div className="min-w-0 flex-1">
              <p className={clsx('font-medium', value === option.value ? 'text-pearl' : 'text-silver')}>{option.label}</p>
              <p className="text-ash mt-0.5 line-clamp-1 text-xs">{option.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
